import { AgencyCampaignSection, AgencyEditorMode } from '../features/agency/model';
import { rootRouteId, type AnyRouter } from '@tanstack/react-router';

import { dashboardT, type DashboardMessageKey } from './messages';
import { pageTitleKeys } from './pageTitles';

export enum BreadcrumbEntity {
  Publisher = 'publisherId',
  Placement = 'placementId',
  Partner = 'demandPartnerId',
  Integration = 'integrationId',
  Network = 'networkId',
  Campaign = 'campaignId',
  Creative = 'creativeId',
  Deal = 'dealId',
  Owner = 'ownerId',
  App = 'appId',
  List = 'listId',
  Targeting = 'targetingId',
  LineItem = 'lineItemId',
  TailoredSupply = 'tailoredSupplyId',
}

export interface BreadcrumbSegment {
  label: string;
  path: string;
  to?: string;
  entity?: BreadcrumbEntity;
  action?: boolean;
  loading?: boolean;
  search?: Record<string, string>;
}

const sectionLabels: Record<string, DashboardMessageKey> = {
  supply: 'nav.supply',
  demand: 'nav.demand',
  campaigns: 'nav.campaigns',
  reporting: 'nav.reporting',
  admin: 'nav.admin',
  a2v: 'nav.a2v',
};
const actionLabels: Record<string, DashboardMessageKey> = {
  create: 'nav.action.create',
  new: 'nav.action.create',
};

// Page headings may describe the whole section; breadcrumbs label each navigation level.
const breadcrumbLabelKeys: Record<string, DashboardMessageKey> = {
  ...pageTitleKeys,
  '/supply/lists/all': 'lists.tabs.all',
  '/supply/lists/rewrite': 'lists.tabs.rewrite',
  '/supply/lists/schain': 'lists.tabs.schain',
  '/supply/blocked-list/creatives': 'blockedList.tabs.creatives',
  '/supply/ads-txt/records': 'adsTxt.tabs.records',
  '/supply/ads-txt/apps': 'adsTxt.tabs.apps',
  '/supply/ads-txt/domains': 'adsTxt.tabs.domains',
  '/campaigns/supply/apps': 'supply.tabs.apps',
  '/campaigns/supply/domains': 'supply.tabs.domains',
  '/campaigns/supply/tailored-supply': 'supply.tabs.tailored',
  '/campaigns/creatives/display': 'creatives.tab.display',
  '/campaigns/creatives/video': 'creatives.tab.video',
  '/supply/publishers/$publisherId/details/app-requests': 'publisherDetails.tabs.appRequests',
  '/supply/publishers/$publisherId/details/a2v': 'publisherDetails.tabs.a2v',
};

// Index routes render a tab without adding another segment to the URL.
const indexTabKeys: Record<string, DashboardMessageKey> = {
  '/supply/apps': 'supplyApps.tabs.apps',
  '/reporting/saved-reports': 'savedReports.tabs.saved',
};

export function isKnownBreadcrumbRoute(router: Pick<AnyRouter, 'matchRoutes'>, path: string) {
  const match = router.matchRoutes(path, {}).at(-1);
  return Boolean(
    match &&
    match.routeId !== rootRouteId &&
    !('**' in match.params) &&
    !('_splat' in match.params),
  );
}

export function resolveBreadcrumbs(
  pathname: string,
  routePath: string,
  isKnownRoute: (path: string) => boolean,
  rootTitle: string,
): BreadcrumbSegment[] {
  const agencyRoot = /^\/a\/[^/]+/.exec(pathname)?.[0];
  if (agencyRoot) {
    const root = { label: dashboardT('agencyWorkspace.title'), path: agencyRoot };
    return pathname.replace(/\/$/, '') === agencyRoot
      ? [root]
      : [
          { ...root, to: agencyRoot },
          { label: rootTitle, path: pathname },
        ];
  }
  const segments = pathname.split('/').filter(Boolean);
  const template = routePath.split('/').filter(Boolean);
  if (!segments.length) return [{ label: rootTitle, path: '/' }];
  const crumbs: BreadcrumbSegment[] = [];
  const integrationRoute = routePath.startsWith('/demand/integrations/');

  for (const [index, segment] of segments.entries()) {
    const path = `/${segments.slice(0, index + 1).join('/')}`;
    const param = template[index]?.startsWith('$') ? template[index].slice(1) : undefined;
    const entity = Object.values(BreadcrumbEntity).find((value) => value === param);
    const previous = crumbs.at(-1);
    const nextIsEntity = template[index + 1]?.startsWith('$');

    // These segments encode editor/API context rather than a separate navigation level.
    if (
      (segment === 'p' && index === 0) ||
      (!param && segment === 'edit') ||
      (!param && nextIsEntity && segment === 'overview') ||
      (integrationRoute &&
        ((!param && segment === 'demand' && index > 0) || entity === BreadcrumbEntity.Partner))
    )
      continue;
    if (!param && segment === 'details' && previous?.entity) {
      previous.path = path;
      continue;
    }
    // Placement type is a create-form discriminator, not an entity name.
    if (param && !entity) continue;

    const actionKey = param ? undefined : actionLabels[segment];
    const key =
      (index === 0 ? sectionLabels[segment] : undefined) ??
      breadcrumbLabelKeys[`/${template.slice(0, index + 1).join('/')}`] ??
      actionKey ??
      sectionLabels[segment];
    let label = segment
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
    if (key) label = dashboardT(key);
    if (entity) label = dashboardT('nav.breadcrumb.entity');
    const crumb: BreadcrumbSegment = {
      label,
      path,
      ...(entity ? { entity } : {}),
      ...(actionKey ? { action: true } : {}),
    };
    crumbs.push(crumb);
  }

  // Entity tabs change the view, not the entity's place in the hierarchy.
  const lastEntityIndex = crumbs.map((crumb) => Boolean(crumb.entity)).lastIndexOf(true);
  if (
    lastEntityIndex >= 0 &&
    crumbs[lastEntityIndex]?.path.endsWith('/details') &&
    !crumbs.slice(lastEntityIndex + 1).some((crumb) => crumb.action)
  ) {
    crumbs.splice(lastEntityIndex + 1);
  }

  const indexPath = pathname.replace(/\/$/, '');
  const indexTab = indexTabKeys[indexPath];
  if (indexTab) crumbs.push({ label: dashboardT(indexTab), path: `${indexPath}/` });

  return crumbs.map((crumb, index) => ({
    ...crumb,
    ...(index < crumbs.length - 1 && isKnownRoute(crumb.path) ? { to: crumb.path } : {}),
  }));
}

export function campaignHierarchyRoute(pathname: string, search: Record<string, unknown>) {
  pathname = pathname.replace(/\/+$/, '');
  const workspaceAgencyId = /^\/a\/([^/]+)\/campaigns\//.exec(pathname)?.[1];
  const brandId = /\/campaigns\/brands\/([^/]+)\/details$/.exec(pathname)?.[1];
  const agencyId =
    /^\/campaigns\/agencies\/([^/]+)\/details$/.exec(pathname)?.[1] ??
    (pathname.endsWith('/campaigns/overview') ? workspaceAgencyId : undefined);
  const campaignId =
    /\/campaigns\/campaigns\/overview\/([^/]+)/.exec(pathname)?.[1] ??
    (workspaceAgencyId &&
    (pathname.endsWith('/campaigns/campaigns') || pathname.endsWith('/campaigns/line-items')) &&
    typeof search.campaign === 'string'
      ? search.campaign
      : undefined);
  const lineItemId =
    /\/line-items\/edit\/([^/]+)$/.exec(pathname)?.[1] ??
    (workspaceAgencyId &&
    pathname.endsWith(`/${AgencyCampaignSection.LineItems}`) &&
    typeof search.id === 'string'
      ? search.id
      : undefined);
  const createLineItem =
    pathname.endsWith('/line-items/create') ||
    Boolean(
      workspaceAgencyId &&
      pathname.endsWith(`/${AgencyCampaignSection.LineItems}`) &&
      search.editor === AgencyEditorMode.Create,
    );
  if (!brandId && !agencyId && !campaignId) return null;
  return { workspaceAgencyId, agencyId, brandId, campaignId, lineItemId, createLineItem };
}
